// Plain-text part generator (spec 08 §3: "auto-generated from the HTML when body_text is null").
const NAMED: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  eacute: "é",
  egrave: "è",
  ecirc: "ê",
  euml: "ë",
  agrave: "à",
  acirc: "â",
  ccedil: "ç",
  ocirc: "ô",
  ucirc: "û",
  ugrave: "ù",
  icirc: "î",
  iuml: "ï",
  oelig: "œ",
  Eacute: "É",
  Egrave: "È",
  Ecirc: "Ê",
  Euml: "Ë",
  Agrave: "À",
  Acirc: "Â",
  Ccedil: "Ç",
  Ocirc: "Ô",
  Ucirc: "Û",
  Ugrave: "Ù",
  Icirc: "Î",
  Iuml: "Ï",
  OElig: "Œ",
  euro: "€",
  copy: "©",
  reg: "®",
  laquo: "«",
  raquo: "»",
  rsquo: "’",
  lsquo: "‘",
  hellip: "…",
  mdash: "—",
  ndash: "–",
};

const UPPER = new Set(["AMP", "LT", "GT", "QUOT", "COPY", "REG"]);

export function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);/gi, (m, e: string) => {
    if (e[0] === "#") {
      const cp =
        e[1] === "x" || e[1] === "X"
          ? parseInt(e.slice(2), 16)
          : parseInt(e.slice(1), 10);
      return Number.isFinite(cp) && cp > 0 && cp <= 0x10ffff
        ? String.fromCodePoint(cp)
        : m;
    }
    // Entity names are case-sensitive; only the legacy upper-case forms (&AMP; &LT; &GT; &QUOT; &COPY; &REG;) fall back.
    return NAMED[e] ?? (UPPER.has(e) ? NAMED[e.toLowerCase()] : undefined) ?? m;
  });
}

const stripTags = (s: string) => s.replace(/<[^>]+>/g, "");
// Text we emit ourselves is re-escaped so the single final decode pass restores it exactly once.
const protect = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;");
// In a template literal, \b must be written \b (a bare \b would be a backspace character).
const BLOCK = "p|div|h[1-6]|table|ul|ol|blockquote";

// Removes <div ... display:none> blocks, tracking nested divs with a depth scan.
function removeHiddenDivs(html: string): string {
  const open = /<div\b[^>]*display:\s*none[^>]*>/i;
  let s = html;
  for (let m = open.exec(s); m; m = open.exec(s)) {
    const tag = /<(\/?)div\b[^>]*>/gi;
    tag.lastIndex = m.index + m[0].length;
    let depth = 1;
    let end = -1;
    for (let t = tag.exec(s); t; t = tag.exec(s)) {
      depth += t[1] ? -1 : 1;
      if (depth === 0) {
        end = t.index + t[0].length;
        break;
      }
    }
    // Unbalanced markup: drop only the opening tag so visible content is never lost.
    s = s.slice(0, m.index) + s.slice(end === -1 ? m.index + m[0].length : end);
  }
  return s;
}

export function htmlToText(html: string): string {
  // A closing tag may carry spaces (`</script >`); an unclosed script or style drops the rest (a browser renders none
  // of it either).
  let s = html
    .replace(/<(head|style|script|title)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, "")
    .replace(/<(script|style)\b[^>]*>[\s\S]*$/i, "");
  s = removeHiddenDivs(s);
  s = s.replace(
    /<a(?=\s)[^>]*?\shref\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'>]+))[^>]*>([\s\S]*?)<\/a>/gi,
    (
      _m,
      _q,
      d: string | undefined,
      sq: string | undefined,
      u: string | undefined,
      inner: string,
    ) => {
      const href = decodeEntities(d ?? sq ?? u ?? "");
      const text = decodeEntities(stripTags(inner)).trim();
      if (!href || href.startsWith("#")) return protect(text);
      if (!text || text === href || `mailto:${text}` === href) {
        return protect(href.replace(/^mailto:/, ""));
      }
      return protect(`${text} (${href})`);
    },
  );
  s = s.replace(
    /<img\b[^>]*\salt\s*=\s*("([^"]*)"|'([^']*)')[^>]*>/gi,
    (_m, _q, a?: string, b?: string) => protect(decodeEntities(a ?? b ?? "")),
  );
  s = s.replace(/<br\s*\/?>/gi, "\n");
  s = s.replace(/<li\b[^>]*>/gi, "\n- ");
  s = s.replace(/<\/t[dh]>/gi, " ");
  s = s.replace(/<\/tr>/gi, "\n");
  s = s.replace(new RegExp(`</(${BLOCK})>`, "gi"), "\n\n");
  s = s.replace(new RegExp(`<(${BLOCK})\\b[^>]*>`, "gi"), "\n\n");
  s = decodeEntities(stripTags(s));
  return s
    .split("\n")
    .map((l) => l.replace(/[^\S\n]+/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
